/**
 * PostHog experiment / feature-flag keys.
 *
 * One source of truth for flag names so frontend, backend, and PostHog UI all
 * reference the same string. When adding a new experiment, document the flag
 * in `docs/experiments/<flag-key>.md`.
 */
export const EXPERIMENT_FLAGS = {
  /**
   * scio3b-split: the onboarding split test on the signup to onboarding
   * experience. Started as a 50/50 A/B (scio3a control vs scio3b), now a
   * 30/30/40 A/B/C with scio3c added to this same flag:
   *   scio3a (control, 30%), scio3b (full treatment, 30%),
   *   scio3c (treatment without the onboarding survey, 40%).
   * scio3c was added to this existing flag rather than a new flag so already
   * bucketed users stay pinned via company.experiments['scio3b-split'] and the
   * flag's ensure_experience_continuity, and never re-bucket.
   * See `docs/experiments/scio3b-split.md`.
   */
  SCIO3B_SPLIT: 'scio3b-split',

  /**
   * onboarding-text: 3-arm (a/b/c) copy test on the WelcomeScreen "you will need
   * a device" line. Server-decided via the OptiGrowth decider (NOT PostHog) and
   * consumed through `useExperimentVariant`. Arms: `a` (control, current copy) |
   * `b` | `c`. See `docs/experiments/onboarding-text.md`.
   */
  ONBOARDING_TEXT: 'onboarding-text',

  /**
   * account-deletion-text: 2-arm (e/f) copy test on the Company Settings →
   * Delete Account section ("Permanently remove your account" line).
   * Server-decided via the OptiGrowth decider (NOT PostHog) and consumed
   * through `useExperimentVariant`. Arms: `e` (control, current copy) | `f`
   * (same copy with a trailing period). Renamed from `testing2` pre-launch
   * (2026-07-06). See `docs/experiments/account-deletion-text.md`.
   */
  ACCOUNT_DELETION_TEXT: 'account-deletion-text',
} as const;

export type ExperimentFlagKey = (typeof EXPERIMENT_FLAGS)[keyof typeof EXPERIMENT_FLAGS];

/**
 * Variants for the scio3b-split onboarding test.
 * - `scio3a`: control (no survey, no checklist).
 * - `scio3b`: full treatment (onboarding survey + Get Started checklist + nav).
 * - `scio3c`: treatment without the onboarding survey (checklist + nav only).
 */
export type OnboardingVariant = 'scio3a' | 'scio3b' | 'scio3c';
